import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DivergenceChapter, DivergenceIndex } from '../data/types';
import './DivergenceBraid.css';

interface Props {
  index: DivergenceIndex;
  mode: 'overview' | 'detail';
  chapterN: number | null;
  onChapterEnter: (n: number) => void;
  onVerseSelect: (n: number) => void;
}

interface HoverCard {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  subtitle: string;
  score: number;
}

const VIEW_W = 980;
const VIEW_H = 320;
const MID_Y = VIEW_H / 2 - 6;

const BOOK_LABEL_TA: Record<string, string> = {
  aram: 'அறம்',
  porul: 'பொருள்',
  inbam: 'இன்பம்',
};

export default function DivergenceBraid({
  index,
  mode,
  chapterN,
  onChapterEnter,
  onVerseSelect,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useRef(false);
  const [card, setCard] = useState<HoverCard>({
    visible: false, x: 0, y: 0, title: '', subtitle: '', score: 0,
  });

  useLayoutEffect(() => {
    reducedMotion.current =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const chapter = useMemo<DivergenceChapter | null>(() => {
    if (mode !== 'detail' || chapterN == null) return null;
    return index.chapters.find((c) => c.n === chapterN) ?? null;
  }, [index, mode, chapterN]);

  const items = useMemo<{ score: number; label: string; kural?: number; chapter?: DivergenceChapter }[]>(() => {
    if (mode === 'overview') {
      return index.chapters.map((c) => ({
        score: c.mean_intensity,
        label: String(c.n),
        chapter: c,
      }));
    }
    if (!chapter) return [];
    return chapter.verse_ns.map((n) => {
      const v = index.verses[String(n)];
      return { score: v?.intensity ?? 0, label: String(n), kural: n };
    });
  }, [index, mode, chapter]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || items.length === 0) return;

    const NS = 'http://www.w3.org/2000/svg';
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const defs = document.createElementNS(NS, 'defs');
    svg.appendChild(defs);
    const filter = document.createElementNS(NS, 'filter');
    filter.setAttribute('id', 'braid-soft');
    filter.setAttribute('x', '-30%');
    filter.setAttribute('y', '-30%');
    filter.setAttribute('width', '160%');
    filter.setAttribute('height', '160%');
    const blur = document.createElementNS(NS, 'feGaussianBlur');
    blur.setAttribute('stdDeviation', '2.5');
    blur.setAttribute('result', 'b');
    filter.appendChild(blur);
    const merge = document.createElementNS(NS, 'feMerge');
    const mn1 = document.createElementNS(NS, 'feMergeNode');
    mn1.setAttribute('in', 'b');
    merge.appendChild(mn1);
    const mn2 = document.createElementNS(NS, 'feMergeNode');
    mn2.setAttribute('in', 'SourceGraphic');
    merge.appendChild(mn2);
    filter.appendChild(merge);
    defs.appendChild(filter);

    const bandsGroup = document.createElementNS(NS, 'g');
    svg.appendChild(bandsGroup);

    const ribbon = document.createElementNS(NS, 'path');
    ribbon.setAttribute('class', 'braid__ribbon');
    svg.appendChild(ribbon);

    const glowM = document.createElementNS(NS, 'path');
    glowM.setAttribute('class', 'braid__glow braid__glow--m');
    glowM.setAttribute('filter', 'url(#braid-soft)');
    svg.appendChild(glowM);
    const glowP = document.createElementNS(NS, 'path');
    glowP.setAttribute('class', 'braid__glow braid__glow--p');
    glowP.setAttribute('filter', 'url(#braid-soft)');
    svg.appendChild(glowP);

    const pathM = document.createElementNS(NS, 'path');
    pathM.setAttribute('class', 'braid__thread braid__thread--m');
    svg.appendChild(pathM);
    const pathP = document.createElementNS(NS, 'path');
    pathP.setAttribute('class', 'braid__thread braid__thread--p');
    svg.appendChild(pathP);

    const marksGroup = document.createElementNS(NS, 'g');
    svg.appendChild(marksGroup);
    const labelsGroup = document.createElementNS(NS, 'g');
    svg.appendChild(labelsGroup);
    const hitsGroup = document.createElementNS(NS, 'g');
    svg.appendChild(hitsGroup);

    const padL = mode === 'overview' ? 26 : 44;
    const stepW = items.length > 1 ? (VIEW_W - padL - 20) / (items.length - 1) : 0;
    const X = (i: number) => padL + i * stepW;

    if (mode === 'overview') {
      const bandDefs: { book: string; from: number; to: number }[] = [];
      let currentBook = index.chapters[0]?.book;
      let start = 0;
      for (let i = 1; i < index.chapters.length; i++) {
        if (index.chapters[i].book !== currentBook) {
          bandDefs.push({ book: currentBook!, from: start, to: i - 1 });
          currentBook = index.chapters[i].book;
          start = i;
        }
      }
      bandDefs.push({ book: currentBook!, from: start, to: index.chapters.length - 1 });

      for (const b of bandDefs) {
        const rect = document.createElementNS(NS, 'rect');
        const x1 = X(b.from) - 3;
        const x2 = X(b.to) + 3;
        rect.setAttribute('x', String(x1));
        rect.setAttribute('y', '0');
        rect.setAttribute('width', String(x2 - x1));
        rect.setAttribute('height', String(VIEW_H));
        rect.setAttribute('class', `braid__band braid__band--${b.book}`);
        bandsGroup.appendChild(rect);
        const label = document.createElementNS(NS, 'text');
        label.setAttribute('x', String((x1 + x2) / 2));
        label.setAttribute('y', '22');
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('class', 'braid__band-label');
        label.setAttribute('lang', 'ta');
        label.textContent = BOOK_LABEL_TA[b.book];
        bandsGroup.appendChild(label);
      }
    }

    const markMEls: SVGCircleElement[] = [];
    const markPEls: SVGCircleElement[] = [];
    const hoverRefs: (() => void)[] = [];

    for (let i = 0; i < items.length; i++) {
      if (mode === 'detail') {
        const mM = document.createElementNS(NS, 'circle');
        mM.setAttribute('cx', String(X(i)));
        mM.setAttribute('cy', String(MID_Y));
        mM.setAttribute('r', '4.5');
        mM.setAttribute('class', 'braid__mark braid__mark--m');
        marksGroup.appendChild(mM);
        markMEls.push(mM);

        const mP = document.createElementNS(NS, 'circle');
        mP.setAttribute('cx', String(X(i)));
        mP.setAttribute('cy', String(MID_Y));
        mP.setAttribute('r', '4.5');
        mP.setAttribute('class', 'braid__mark braid__mark--p');
        marksGroup.appendChild(mP);
        markPEls.push(mP);

        const t = document.createElementNS(NS, 'text');
        t.setAttribute('x', String(X(i)));
        t.setAttribute('y', String(VIEW_H - 10));
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('class', 'braid__label braid__label--verse');
        t.textContent = items[i].label;
        labelsGroup.appendChild(t);
      } else if (i % 10 === 0 || i === items.length - 1) {
        const t = document.createElementNS(NS, 'text');
        t.setAttribute('x', String(X(i)));
        t.setAttribute('y', String(VIEW_H - 8));
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('class', 'braid__label braid__label--chapter');
        t.textContent = items[i].label;
        labelsGroup.appendChild(t);
      }

      const hit = document.createElementNS(NS, 'rect');
      hit.setAttribute('x', String(X(i) - stepW / 2));
      hit.setAttribute('y', '0');
      hit.setAttribute('width', String(stepW));
      hit.setAttribute('height', String(VIEW_H));
      hit.setAttribute('class', 'braid__hit');
      hitsGroup.appendChild(hit);

      const onEnter = () => {
        hoverIdx = i;
        const it = items[i];
        const stageRect = stageRef.current?.getBoundingClientRect();
        if (!stageRect) return;
        const cx = (X(i) / VIEW_W) * stageRect.width;
        if (mode === 'overview' && it.chapter) {
          setCard({
            visible: true,
            x: Math.min(stageRect.width - 260, Math.max(10, cx + 14)),
            y: 14,
            title: `அதிகாரம் ${it.chapter.n}`,
            subtitle: it.chapter.title_en,
            score: it.chapter.mean_intensity,
          });
        } else if (it.kural != null) {
          setCard({
            visible: true,
            x: Math.min(stageRect.width - 260, Math.max(10, cx + 14)),
            y: 14,
            title: `குறள் ${it.kural}`,
            subtitle: 'click to open the reading room',
            score: it.score,
          });
        }
      };
      const onLeave = () => {
        if (hoverIdx === i) {
          hoverIdx = -1;
          setCard((c) => ({ ...c, visible: false }));
        }
      };
      const onClick = () => {
        const it = items[i];
        if (mode === 'overview' && it.chapter) {
          onChapterEnter(it.chapter.n);
        } else if (it.kural != null) {
          onVerseSelect(it.kural);
        }
      };
      hit.addEventListener('mouseenter', onEnter);
      hit.addEventListener('mouseleave', onLeave);
      hit.addEventListener('click', onClick);
      hoverRefs.push(() => {
        hit.removeEventListener('mouseenter', onEnter);
        hit.removeEventListener('mouseleave', onLeave);
        hit.removeEventListener('click', onClick);
      });
    }

    let hoverIdx = -1;
    let tension = 0;
    let start = performance.now();
    let rafId = 0;

    const maxGap = mode === 'detail' ? 158 : 140;
    const drawMs = reducedMotion.current ? 1 : mode === 'detail' ? 1500 : 2100;

    function threadY(i: number, sign: number, now: number, prog: number): number {
      const amp = mode === 'detail' ? 2.4 : 1.1;
      const sway = (reducedMotion.current ? 0 : amp) * Math.sin(now / 1500 + i * 0.6) * sign;
      const pull = i === hoverIdx ? 1 + 0.18 * tension : 1;
      const s = items[i].score;
      return MID_Y + (sign * ((6 + s * maxGap) * pull * prog)) / 2 + sway;
    }
    function pathOf(pts: [number, number][]): string {
      if (pts.length === 0) return '';
      let d = `M ${pts[0][0]} ${pts[0][1]}`;
      for (let i = 1; i < pts.length; i++) {
        const cx = (pts[i - 1][0] + pts[i][0]) / 2;
        d += ` C ${cx} ${pts[i - 1][1]}, ${cx} ${pts[i][1]}, ${pts[i][0]} ${pts[i][1]}`;
      }
      return d;
    }

    function frame(now: number) {
      const t = now - start;
      const eased = 1 - Math.pow(1 - Math.min(1, t / drawMs), 3);
      tension += ((hoverIdx >= 0 ? 1 : 0) - tension) * 0.12;
      const up: [number, number][] = [];
      const dn: [number, number][] = [];
      for (let i = 0; i < items.length; i++) {
        up.push([X(i), threadY(i, -1, now, eased)]);
        dn.push([X(i), threadY(i, 1, now, eased)]);
      }
      const dM = pathOf(up);
      const dP = pathOf(dn);
      pathM.setAttribute('d', dM);
      glowM.setAttribute('d', dM);
      pathP.setAttribute('d', dP);
      glowP.setAttribute('d', dP);
      ribbon.setAttribute(
        'd',
        'M ' +
          up.map((p) => `${p[0]} ${p[1]}`).join(' L ') +
          ' L ' +
          dn.slice().reverse().map((p) => `${p[0]} ${p[1]}`).join(' L ') +
          ' Z',
      );
      ribbon.setAttribute('opacity', String(eased));

      if (mode === 'detail') {
        for (let i = 0; i < items.length; i++) {
          markMEls[i]?.setAttribute('cy', String(up[i][1]));
          markPEls[i]?.setAttribute('cy', String(dn[i][1]));
        }
      }
      rafId = requestAnimationFrame(frame);
    }
    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
      for (const cleanup of hoverRefs) cleanup();
    };
  }, [items, mode, index, onChapterEnter, onVerseSelect]);

  return (
    <div ref={stageRef} className="braid__stage">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={
          mode === 'overview'
            ? 'Two threads of commentary divergence across 133 chapters'
            : `Verse divergence within chapter ${chapterN}`
        }
      />
      {card.visible && (
        <div className="braid__card" style={{ left: card.x, top: card.y }} role="status">
          <span
            className="braid__card-score"
            style={{ color: scoreColor(card.score) }}
          >
            {card.score.toFixed(2)}
          </span>
          <span className="braid__card-title">{card.title}</span>
          <span className="braid__card-sub">{card.subtitle}</span>
        </div>
      )}
    </div>
  );
}

/** Map continuous intensity to a token, used by the hover card. */
function scoreColor(s: number): string {
  if (s > 0.6) return 'var(--paal-inbam)';
  if (s > 0.35) return 'var(--paal-aram)';
  return 'var(--edge-thematic)';
}
